import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  createStyles,
  FormControlLabel,
  Grid,
  makeStyles,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";

import _ from "lodash";
import WithLoading from "../../../../utils/WithLoading";
import { CustomTheme } from "../../../../types/customTheme";
import { handleDownloadExcel } from "./convert/handleExcel";
import LocationDetailsCard from "../../../../components/common/LocationDetailsCard";
import MenuButtonCommon from "../../../../components/buttons/MenuButtonCommon";
import GetAppIcon from "@material-ui/icons/GetApp";
import SwitchCommon from "../../../../components/switches/SwitchCommon";
import NormalSalesTable from "./NormalSalesTable";
import ChartSalesTable from "./ChartSalesTable";
import { handleDownloadPdf } from "./convert/handlePdf";
import { handleDownloadPdfNew } from "./convert/handlePdfNew";
import { handleDownloadExcelNew } from "./convert/handleExcelNew";

export interface ShiftHeaderProps {
  isLoading: any;
  startDate: any;
  endDate: any;
  filterDetails: any;
  locationSelectedList: any;
  handleOnClickText: any;
  driverSummaryNodeList: any;
}

const dropDownDaysList = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const useStyles = makeStyles((theme: any) =>
  createStyles({
    tableContainer: {
      overflowX: "auto", // Enable horizontal scroll if needed
      backgroundColor: "transparent",
    },
    table: {
      borderCollapse: "separate",
      borderSpacing: "4px", // Adjust spacing here (horizontal vertical)
    },

    dateCell: {
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      overflow: "hidden",
      padding: "8px",
      borderBottom: "none",
    },
    hourCell: {
      position: "sticky",
      width: "150px",
      left: 0,
      backgroundColor: theme.palette.background.default, // Ensure background is solid for fixed column
      zIndex: 1, // Ensure it stays on top of other cells when scrolling
      fontWeight: "bold",
      borderBottom: "none",
    },
    titleColor: {
      color: theme.palette.custom.orange.contrastText,
    },
    hourHeaderCell: {
      position: "sticky",
      width: "150px",
      left: 0,
      backgroundColor: theme.palette.background.default,
      zIndex: 3, // Ensure the header stays on top
      fontWeight: "bold",
      borderBottom: "none",
    },
    autoComplete: {
      marginTop: "4px",
      "& .MuiAutocomplete-input": {
        color: theme.palette.custom.orange.contrastText,
      },
      "& .MuiInputBase-root": {
        color: "inherit",
        backgroundColor: theme.palette.background.entity_highlight_background,
        borderRadius: 10,
      },
      [`& fieldset`]: {
        borderRadius: 10,
        border: "none",
        color: theme.palette.custom.orange.contrastText,
      },
    },
  }),
);

/**
 * HourlySalesReportNode Component:
 *
 * This component handles the header section of the Shift component, including
 * sorting controls, location selection, and shift generation. It displays shift
 * information in a table-like format with sortable columns and provides options
 * for selecting shifts, generating reports, and handling user interactions.
 */
const HourlySalesReportNode: React.FunctionComponent<ShiftHeaderProps> = ({
  isLoading,
  startDate,
  endDate,
  filterDetails,
  locationSelectedList,
  handleOnClickText,
  driverSummaryNodeList,
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedRoleObj, setSelectedRoleObj] = useState<any>({
    id: "all",
    label: "Sales Count & Total",
  });
  const [isShowChart, setIsShowChart] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedDaysList, setSelectedDaysList] =
    useState<any>(dropDownDaysList);

  const openExport = Boolean(anchorEl);
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  function generateMonthlyReport(
    input: any,
    startDate: string,
    endDate: string,
  ) {
    const start = new Date(startDate);

    const end = new Date(endDate);
    const startYear = start.getFullYear();
    const endYear = end.getFullYear();

    const structure: any[] = [];
    const totalCount: number[] = [];
    const totalValue: number[] = [];

    for (let year = startYear; year <= endYear; year++) {
      const firstMonth = year === startYear ? start.getMonth() : 0;
      const lastMonth = year === endYear ? end.getMonth() : 11;

      const yearData = [];

      for (let m = firstMonth; m <= lastMonth; m++) {
        const monthData = input[year]?.find((d: any) => d.month === m + 1);
        const count = monthData?.saleCount || 0;
        const value = monthData?.saleTotal || 0;

        yearData.push({
          month: monthNames[m],
          count,
          value,
        });

        totalCount.push(count);
        totalValue.push(value);
      }

      structure.push({
        year,
        data: yearData,
      });
    }

    return { structure, totalCount, totalValue };
  }
  const [value, setValue] = useState<any>([]);
  const [dummyData, setDummyData] = useState<any>([]);
  const [valueTotal, setValueTotal] = useState<any>([]);
  const [columns, setColumns] = useState<any>([]);
  const [nodeData, setNodeData] = useState<any>([]);

  function generateMonthlySummary(input: any, startDate: any, endDate: any) {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const result: any[] = [];

    // Totals
    let totalCard = 0;
    let totalCash = 0;
    let totalDiscount = 0;
    let totalOtherPayments = 0;
    let totalSaleCount = 0;
    let totalSaleTotal = 0;
    let totalAmount = 0;

    const monthsMap = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    let current = new Date(start.getFullYear(), start.getMonth(), 1);

    while (current <= end) {
      const year = current.getFullYear();
      const month = current.getMonth() + 1; // JS months are 0-based

      const monthData = (input[year] || []).find((m: any) => m.month === month);

      const card = monthData ? monthData.card : 0;
      const cash = monthData ? monthData.cash : 0;
      const discount = monthData ? monthData.discount : 0;
      const otherPayments = monthData ? monthData.otherPayments : 0;
      const saleCount = monthData ? monthData.saleCount : 0;
      const saleTotal =
        (monthData ? monthData.saleTotal : 0) -
        (monthData ? monthData.discount : 0);
      const total = monthData ? monthData.total : 0;

      result.push({
        month: `${year} ${monthsMap[current.getMonth()]}`,
        card,
        cash,
        discount,
        otherPayments,
        saleCount,
        saleTotal,
        total,
      });

      // accumulate totals
      totalCard += card;
      totalCash += cash;
      totalDiscount += discount;
      totalOtherPayments += otherPayments;
      totalSaleCount += saleCount;
      totalSaleTotal += saleTotal;
      totalAmount += total;

      current.setMonth(current.getMonth() + 1);
    }

    // Add totals row
    result.push({
      month: "Total",
      card: totalCard,
      cash: totalCash,
      discount: totalDiscount,
      otherPayments: totalOtherPayments,
      saleCount: totalSaleCount,
      saleTotal: totalSaleTotal,
      total: totalAmount,
    });

    return result;
  }

  useEffect(() => {
    const { structure, totalCount, totalValue }: any = generateMonthlyReport(
      driverSummaryNodeList,
      startDate,
      endDate,
    );

    const data = generateMonthlySummary(
      driverSummaryNodeList,
      startDate,
      endDate,
    );
    setNodeData(data);
   
    const uniqueSortedCount = Array.from(
      new Set(totalCount.filter((v: any) => v !== 0)),
    ).sort((a: any, b: any) => a - b);
    const uniqueSortedValue = Array.from(
      new Set(totalValue.filter((v: any) => v !== 0)),
    ).sort((a: any, b: any) => a - b);
    setDummyData(structure);
    setValue(uniqueSortedCount);
    setValueTotal(uniqueSortedValue);

    if (structure[0]?.data) {
      const columns: any = [];
      structure[0].data.map((data: any) => columns.push(data.month));
      setColumns(columns);
    }
  }, [driverSummaryNodeList, selectedDaysList, selectedRoleObj]);
  const theme: CustomTheme = useTheme();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const daysWeekly = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const handleShowChart = () => {
    setIsShowChart(!isShowChart);
  };
  const maxWidthLocationChips = useMediaQuery("(max-width: 600px)");

  return (
    <>
      <Grid
        container
        style={{
          marginBottom: "20px",
          marginTop: "8px",
        }}
      >
        <Grid item xs={12}>
          <div
            style={
              maxWidthLocationChips
                ? { display: "block" }
                : {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }
            }
          >
            <LocationDetailsCard
              locationSelectedList={locationSelectedList}
              handleOnClickText={handleOnClickText}
            />
            <div style={{ display: "flex", alignItems: "center" }}>
              <FormControlLabel
                style={{ marginRight: "8px" }}
                control={<SwitchCommon />}
                checked={isShowChart}
                onChange={handleShowChart}
                label="Show Heatmap"
                labelPlacement="start"
              />
              {true && (
                <div
                  style={
                    maxWidthLocationChips
                      ? {
                          display: "flex",
                          justifyContent: "end",
                          marginTop: "8px",
                        }
                      : { marginLeft: "16px" }
                  }
                >
                  <MenuButtonCommon
                    id="basic-button"
                    variant="contained"
                    style={{
                      fontSize: 11,
                      height: "38px",
                      paddingLeft: "16px",
                      paddingRight: "16px",
                    }}
                    color="blue"
                    aria-controls={open ? "basic-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={handleClick}
                  >
                    <GetAppIcon />
                    <Typography
                      variant="body1"
                      style={{ paddingLeft: "16px", color: "white" }}
                    >
                      Export
                    </Typography>
                  </MenuButtonCommon>
                  <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={openExport}
                    onClose={handleClose}
                    MenuListProps={{
                      "aria-labelledby": "basic-button",
                    }}
                    PaperProps={{
                      elevation: 0,
                      style: {
                        borderRadius: "10px",
                        border: `1px solid ${theme.palette.background.entity_border}`,
                      },
                    }}
                    style={{ top: "56px" }}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        handleDownloadPdfNew(
                          [
                            "Month",
                            "Orders",
                            "Sub Total",
                            "Discount",
                            "Total",
                            "Cash Pay",
                            "Card",
                            "Other Pay",
                          ],
                          nodeData,
                          filterDetails,
                        );
                        handleClose();
                      }}
                    >
                      Export as PDF
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleDownloadExcelNew(
                          [
                            "Month",
                            "Orders",
                            "Sub Total",
                            "Discount",
                            "Total",
                            "Cash Pay",
                            "Card",
                            "Other Pay",
                          ],
                          nodeData,
                          filterDetails,
                        );
                        handleClose();
                      }}
                    >
                      Export as CSV
                    </MenuItem>
                  </Menu>
                </div>
              )}
            </div>
          </div>
        </Grid>
      </Grid>
      <TableContainer className={classes.tableContainer}>
        {isShowChart ? (
          <ChartSalesTable dummyData={dummyData} valueTotal={valueTotal} />
        ) : (
          <NormalSalesTable dummyData={nodeData} />
        )}
      </TableContainer>
    </>
  );
};

export default WithLoading(HourlySalesReportNode);
